import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { SettingsSection } from '@frontend/settings-ui';
import { theme } from '@frontend/theme';
import { SearchField, SpinningLoader, Text, useDebouncedValue, useFormField, styles } from '@frontend/design-system';
import { useGetOperatoryV3Data } from '../../../hooks/scheduler-v3/use-get-operatory-v3-data';
import { useSchedulingLocationInfo } from '../../../hooks/use-scheduling-location-info';
import { NoProvidersIllustration } from '../../../images';
import { EmptyState } from '../empty-state';
import { OperatoryContainer } from '../operatory-container';

type ScheduleDefinitionTemplatesPageProps = {
  locationId: string;
  hasAccess: boolean;
};

export const ScheduleDefinitionTemplatesPage = ({ locationId, hasAccess }: ScheduleDefinitionTemplatesPageProps) => {
  const { t } = useTranslation('schedule-settings');
  const tokens = useTranslationTokens();

  const { selectedLocationIds } = useSchedulingLocationInfo();
  const selectedLocationId = locationId || selectedLocationIds[0] || '';
  const searchField = useFormField({ type: 'text' });
  const debouncedValue = useDebouncedValue(searchField.value, 500);

  const { data: operatoriesData, isFetching: isFetchingOperatoriesData } = useGetOperatoryV3Data({
    selectedLocationId,
    selectedLocationIds: [selectedLocationId],
    debouncedSearchValue: debouncedValue || undefined,
  });

  const sortedOperatories = operatoriesData?.operatories?.sort((a, b) => {
    // First sort by active status first
    const activeSort = (b.details?.act ? 1 : 0) - (a.details?.act ? 1 : 0);
    if (activeSort !== 0) return activeSort;

    // Then alphabetize by display name or external name
    const aName = a.displayName || a.externalName || '';
    const bName = b.displayName || b.externalName || '';
    return aName.localeCompare(bName);
  });

  const isLoading = isFetchingOperatoriesData;

  return (
    <SettingsSection>
      <SearchField
        {...searchField}
        label=''
        name={t('operatories')}
        placeholder={t('Search operatories by name')}
        css={{ marginBottom: theme.spacing(2), maxWidth: 300, marginLeft: 'auto' }}
      />
      {isLoading ? (
        <div css={styles.flexCenter}>
          <SpinningLoader size='small' />
        </div>
      ) : (
        <>
          {!hasAccess && <Text>{t('Access for Weave Support only')}</Text>}
          {hasAccess && !!sortedOperatories?.length ? (
            sortedOperatories?.map((operatory) => (
              <OperatoryContainer key={operatory.id} operatory={operatory} locationId={locationId} />
            ))
          ) : (
            <EmptyState
              title={t('No {{operatories}} Found', { operatories: tokens.operatories })}
              description={t(
                "To add availability, {{operatories}} must be mapped and added to your organization's locations.",
                { operatories: tokens.operatories }
              )}
              image={<NoProvidersIllustration />}
            />
          )}
        </>
      )}
    </SettingsSection>
  );
};
